import { useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  ArrowDownLeft, ArrowUpRight, ArrowLeftRight, BookOpen, Boxes, ChartNoAxesColumn, Home, LayoutGrid, Package, Plus,
  ReceiptText, ShoppingBag, Truck, Users, Wallet, Settings, Undo2,
} from 'lucide-react';
import { useBiz, useSession } from '@/lib/session.tsx';
import { cx } from '@/ui/cx.ts';
import { Sheet } from '@/ui/Sheet.tsx';
import { IconTile } from '@/ui/Layout.tsx';

export function Shell() {
  const [quick, setQuick] = useState(false);
  return (
    <div className="min-h-dvh lg:pl-[248px]">
      <Sidebar onQuick={() => setQuick(true)} />
      <main className="min-h-dvh">
        <Outlet />
      </main>
      <BottomNav onQuick={() => setQuick(true)} />
      <QuickActions open={quick} onOpenChange={setQuick} />
    </div>
  );
}

function useNavItems() {
  const { can, business } = useBiz();
  const items: Array<{ to: string; label: string; icon: ReactNode; show: boolean }> = [
    { to: '/', label: 'Home', icon: <Home />, show: true },
    { to: '/sales', label: 'Sales', icon: <ReceiptText />, show: true },
    { to: '/parties', label: 'Khata', icon: <Users />, show: can('parties.balances') || can('parties.create') || can('sales.create') },
    { to: '/money', label: 'Money', icon: <Wallet />, show: can('money.collect') || can('money.pay') || can('money.expense') || can('money.balances') },
    { to: '/purchases', label: 'Purchases', icon: <ShoppingBag />, show: can('purchases.create') && business.modules.purchases },
    { to: '/items', label: 'Items', icon: <Package />, show: true },
    { to: '/stock', label: 'Stock', icon: <Boxes />, show: business.stockEnabled && can('stock.view') },
    { to: '/fleet', label: 'Vehicles', icon: <Truck />, show: business.modules.fleet },
    { to: '/reports', label: 'Reports', icon: <ChartNoAxesColumn />, show: can('reports.profit') || can('reports.daybook') || can('reports.gst') || can('parties.dues_report') },
    { to: '/settings', label: 'Settings', icon: <Settings />, show: true },
  ];
  return items.filter((i) => i.show);
}

function Sidebar({ onQuick }: { onQuick: () => void }) {
  const items = useNavItems();
  const { business } = useBiz();
  return (
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-[248px] flex-col border-r border-line bg-surface px-3 py-4 lg:flex">
      <div className="mb-4 flex items-center gap-2.5 px-2">
        <Logo />
        <div className="min-w-0">
          <p className="truncate text-[15px] font-bold leading-tight">{business.name}</p>
          <p className="text-[12px] text-ink-3">LogBook</p>
        </div>
      </div>
      <button
        type="button"
        onClick={onQuick}
        className="press mb-3 flex h-11 items-center justify-center gap-2 rounded-xl bg-brand font-semibold text-brand-ink"
      >
        <Plus className="size-5" /> New entry
      </button>
      <nav className="flex flex-col gap-0.5">
        {items.map((i) => (
          <NavLink
            key={i.to}
            to={i.to}
            end={i.to === '/'}
            className={({ isActive }) => cx(
              'press flex h-10 items-center gap-3 rounded-xl px-3 text-[14.5px] font-semibold [&_svg]:size-[19px]',
              isActive ? 'bg-surface-2 text-ink' : 'text-ink-2 hover:bg-surface-2/70 hover:text-ink',
            )}
          >
            {i.icon}
            {i.label}
          </NavLink>
        ))}
      </nav>
    </aside>
  );
}

function BottomNav({ onQuick }: { onQuick: () => void }) {
  const { can } = useBiz();
  const location = useLocation();
  const showKhata = can('parties.balances') || can('parties.create') || can('sales.create');
  const tabs = [
    { to: '/', label: 'Home', icon: Home, end: true },
    { to: '/sales', label: 'Sales', icon: ReceiptText },
    null,
    showKhata ? { to: '/parties', label: 'Khata', icon: BookOpen } : { to: '/items', label: 'Items', icon: Package },
    { to: '/more', label: 'More', icon: LayoutGrid },
  ];
  // Hide the nav on form screens so the save button owns the bottom of the screen.
  const hidden = /\/(new|edit|in|out|transfer|return|count|adjust|invite|deliver)(\/|$)/.test(location.pathname)
    || /^\/(setup|login|join)/.test(location.pathname);
  if (hidden) return null;
  return (
    <nav className="safe-bottom fixed inset-x-0 bottom-0 z-30 border-t border-line bg-surface/92 backdrop-blur-xl lg:hidden">
      <div className="mx-auto grid h-[64px] max-w-[640px] grid-cols-5 items-center px-2">
        {tabs.map((t, i) =>
          t === null ? (
            <div key="fab" className="flex justify-center">
              <button
                type="button"
                onClick={onQuick}
                aria-label="New entry"
                className="press -mt-7 flex size-[58px] items-center justify-center rounded-[20px] bg-brand text-brand-ink shadow-float ring-[5px] ring-bg"
              >
                <Plus className="size-7" strokeWidth={2.25} />
              </button>
            </div>
          ) : (
            <NavLink
              key={i}
              to={t.to}
              end={t.end}
              className={({ isActive }) => cx(
                'press flex flex-col items-center justify-center gap-1 text-[11.5px] font-semibold',
                isActive ? 'text-ink' : 'text-ink-3',
              )}
            >
              {({ isActive }) => (
                <>
                  <t.icon className="size-[23px]" strokeWidth={isActive ? 2.2 : 1.8} />
                  {t.label}
                </>
              )}
            </NavLink>
          ),
        )}
      </div>
    </nav>
  );
}

function QuickActions({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const { can, business, me } = useBiz();
  const navigate = useNavigate();
  const go = (to: string) => {
    onOpenChange(false);
    navigate(to);
  };
  const actions = [
    { to: '/sales/new', label: 'New sale', detail: 'Bill a customer', icon: <ReceiptText />, tone: 'brand' as const, show: can('sales.create') },
    { to: '/sales/new?book=1', label: 'Book an order', detail: 'Deliver later', icon: <Package />, tone: 'neutral' as const, show: can('sales.create') },
    { to: '/money/in', label: 'Money in', detail: 'Payment received', icon: <ArrowDownLeft />, tone: 'in' as const, show: can('money.collect') },
    { to: '/money/out', label: 'Money out', detail: 'Pay or spend', icon: <ArrowUpRight />, tone: 'out' as const, show: can('money.pay') || can('money.expense') },
    { to: '/purchases/new', label: 'Purchase', detail: 'Vendor bill', icon: <ShoppingBag />, tone: 'neutral' as const, show: can('purchases.create') && business.modules.purchases },
    { to: '/money/transfer', label: me.cashAccountId && !can('money.transfer') ? 'Hand over cash' : 'Move money', detail: me.cashAccountId && !can('money.transfer') ? 'Give cash to the office' : 'Between accounts', icon: <ArrowLeftRight />, tone: 'neutral' as const, show: can('money.transfer') || !!me.cashAccountId },
    { to: '/sales/new?return=1', label: 'Sales return', detail: 'Goods came back', icon: <Undo2 />, tone: 'neutral' as const, show: can('sales.credit_note') },
  ].filter((a) => a.show);
  return (
    <Sheet open={open} onOpenChange={onOpenChange} title="New entry">
      <div className="grid grid-cols-2 gap-2.5 pb-2">
        {actions.map((a) => (
          <button
            key={a.to}
            type="button"
            onClick={() => go(a.to)}
            className="press flex flex-col items-start gap-3 rounded-[20px] border border-line bg-surface p-3.5 text-left hover:bg-surface-2/60"
          >
            <IconTile tone={a.tone}>{a.icon}</IconTile>
            <span>
              <span className="block text-[15.5px] font-bold text-ink">{a.label}</span>
              <span className="block text-[13px] text-ink-3">{a.detail}</span>
            </span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}

/** The LogAI mark, the same image as the home-screen icon (cached for offline use). */
export function Logo({ size = 36 }: { size?: number }) {
  return <img src="/logai-180.png" width={size} height={size} alt="" aria-hidden className="shrink-0 rounded-[25%]" />;
}

/** The "More" tab on phones: everything not in the bottom bar. */
export function MoreScreen() {
  const items = useNavItems().filter((i) => !['/', '/sales'].includes(i.to));
  const { business, me } = useBiz();
  const { me: session } = useSession();
  return (
    <div className="mx-auto w-full max-w-[640px] px-4 pb-28">
      <header className="safe-top pb-3 pt-5">
        <h1 className="text-[28px] font-bold tracking-[-0.025em]">More</h1>
        <p className="text-[14px] text-ink-2">{business.name} · {me.roleName}</p>
      </header>
      <div className="grid grid-cols-3 gap-2.5">
        {items.map((i) => (
          <NavLink key={i.to} to={i.to} className="press flex aspect-[1/0.92] flex-col items-start justify-between rounded-[20px] border border-line bg-surface p-3.5 shadow-card [&_svg]:size-[22px]">
            <span className="text-ink-2">{i.icon}</span>
            <span className="text-[14.5px] font-bold text-ink">{i.label}</span>
          </NavLink>
        ))}
      </div>
      {session && session.businesses.length > 1 && (
        <p className="mt-6 px-1 text-[13px] text-ink-3">Switch business from the top of the Home screen.</p>
      )}
    </div>
  );
}
